<!--
  Reusable SQL display block with lightweight, dependency-free syntax
  highlighting. Renders a single `<code>` element containing a flat
  sequence of `<span>` tokens — callers wrap it in their own `<pre>`
  (or any other) container so the surface styling (padding,
  background, scroll behaviour) stays a caller concern.

  Why a hand-rolled tokenizer instead of Prism/highlight.js: the embed
  bundle already weighs in around 17 MB / 4.8 MB gzipped, and a SQL
  display block's needs are modest (keywords, strings, numbers,
  comments, positional placeholders). Pulling in a multi-language
  highlighter is the wrong trade. The tokenizer here is a single
  regex pass over the source — see `tokenize()` below.

  The dialect is DuckDB / PostgreSQL-leaning since that's what the
  runtime targets. Identifier-vs-keyword classification is
  case-insensitive on match but preserves the author's original
  casing in the rendered output (so `select` stays `select`).

  Reused / reusable for: the embed-chrome Queries tab today; any
  future SQL preview surface (CodeTab, Query inspector panel, SBI
  authoring previews) should reach for this component rather than
  re-rendering raw `<code>{{ sql }}</code>`.
-->

<script setup lang="ts">
import { computed } from "vue";

/**
 * Token classes emitted by the SQL tokenizer. `text` is the catch-all
 * for everything that doesn't fall into a highlighted category —
 * whitespace, punctuation, operators, unknown identifiers. Rendered
 * without a span class so it inherits the surrounding code colour.
 */
type TokenType =
  | "keyword"
  | "string"
  | "number"
  | "comment"
  | "placeholder"
  | "text";

interface Token {
  type: TokenType;
  text: string;
}

const props = defineProps<{
  /** The SQL source to render. Pre-formatted whitespace is preserved. */
  sql: string;
}>();

/**
 * DuckDB / PostgreSQL-leaning keyword set. Matched case-insensitively;
 * the rendered output preserves the author's original casing. Kept
 * deliberately conservative — including overly-generic identifiers
 * (e.g. column-name-shaped words like `name`, `id`, `count`) here
 * would mis-highlight everyday schemas.
 */
const KEYWORDS = new Set<string>([
  // DML / clauses
  "SELECT", "FROM", "WHERE", "GROUP", "BY", "ORDER", "HAVING",
  "LIMIT", "OFFSET", "FETCH", "FIRST", "LAST", "NEXT", "ONLY",
  "AS", "DISTINCT", "ALL", "WITH", "RECURSIVE", "LATERAL",
  "UNION", "INTERSECT", "EXCEPT", "QUALIFY", "RETURNING",
  // Joins
  "JOIN", "LEFT", "RIGHT", "INNER", "OUTER", "FULL", "CROSS",
  "ON", "USING", "NATURAL",
  // Conditionals
  "AND", "OR", "NOT", "IN", "IS", "NULL", "BETWEEN",
  "LIKE", "ILIKE", "SIMILAR", "TO", "EXISTS",
  "CASE", "WHEN", "THEN", "ELSE", "END", "IF",
  // DML mutations
  "INSERT", "INTO", "VALUES", "UPDATE", "SET", "DELETE",
  "CONFLICT", "DO", "NOTHING", "MERGE", "MATCHED",
  // DDL
  "CREATE", "DROP", "ALTER", "TABLE", "VIEW", "INDEX",
  "TEMP", "TEMPORARY", "REPLACE", "OR",
  "PRIMARY", "KEY", "FOREIGN", "REFERENCES", "CONSTRAINT",
  "DEFAULT", "UNIQUE", "CHECK", "COLUMN", "ADD", "RENAME",
  // Ordering
  "ASC", "DESC", "NULLS",
  // Window / framing
  "OVER", "PARTITION", "WINDOW",
  "ROWS", "RANGE", "GROUPS", "UNBOUNDED",
  "PRECEDING", "FOLLOWING", "CURRENT", "ROW", "TIES",
  // Casts / types — common ones; `CAST` is the form most often hand-written.
  "CAST", "BOOLEAN", "INTEGER", "BIGINT", "SMALLINT",
  "VARCHAR", "TEXT", "CHAR", "DATE", "TIMESTAMP", "TIME",
  "INTERVAL", "DECIMAL", "NUMERIC", "REAL", "DOUBLE", "PRECISION",
  "JSON", "JSONB", "UUID", "BLOB", "BYTEA",
  // Literals that are syntactically keywords, not identifiers.
  "TRUE", "FALSE", "UNKNOWN",
]);

/**
 * Single-pass tokenizer over the SQL source.
 *
 * The regex below uses ordered alternation: each alternative is a
 * distinct capture group, and only one fires per match. Order matters
 * — block comments must beat line comments (a `/*` line that also
 * contains `--` shouldn't fragment), and string literals must beat
 * the identifier rule (a quoted `SELECT` inside a string should not
 * tokenize as a keyword).
 *
 *   1. Block comment `/* … *\/` — non-greedy, multi-line.
 *   2. Line comment `-- …` — runs to end-of-line.
 *   3. String literal `'…'` — embedded `''` is the SQL escape for a
 *      single quote, not a string terminator, so the inner class
 *      accepts either `[^']` or `''`.
 *   4. Number — integer or decimal, word-boundary anchored so we
 *      don't grab the trailing digits of an identifier like `col1`.
 *   5. Positional placeholder `$N` — visible in the runtime's logged
 *      queries before the host-side substitution runs.
 *   6. Identifier — classified as keyword if its upper-case form is
 *      in the KEYWORDS set, otherwise rendered as `text`.
 *
 * Everything else (whitespace, punctuation, operators) flows through
 * as `text` segments between matches. The trailing slice picks up
 * any text after the final match so no characters are dropped.
 */
const TOKEN_RE =
  /(\/\*[\s\S]*?\*\/|--[^\n]*)|('(?:[^']|'')*')|(\b\d+(?:\.\d+)?\b)|(\$\d+)|([A-Za-z_][A-Za-z0-9_]*)/g;

const tokenize = (source: string): Token[] => {
  const out: Token[] = [];
  let cursor = 0;
  // Reset state defensively — the regex literal is module-scoped, and
  // `lastIndex` survives across calls if a prior iteration threw or
  // the regex were reused across components.
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(source)) !== null) {
    if (match.index > cursor) {
      out.push({ type: "text", text: source.slice(cursor, match.index) });
    }
    if (match[1] !== undefined) {
      out.push({ type: "comment", text: match[1] });
    } else if (match[2] !== undefined) {
      out.push({ type: "string", text: match[2] });
    } else if (match[3] !== undefined) {
      out.push({ type: "number", text: match[3] });
    } else if (match[4] !== undefined) {
      out.push({ type: "placeholder", text: match[4] });
    } else if (match[5] !== undefined) {
      const word = match[5];
      out.push({
        type: KEYWORDS.has(word.toUpperCase()) ? "keyword" : "text",
        text: word,
      });
    }
    cursor = TOKEN_RE.lastIndex;
  }
  if (cursor < source.length) {
    out.push({ type: "text", text: source.slice(cursor) });
  }
  return out;
};

const tokens = computed<Token[]>(() => tokenize(props.sql));
</script>

<template>
  <code class="sql-block"
    ><span
      v-for="(t, i) in tokens"
      :key="i"
      :class="t.type !== 'text' ? `sql-tok sql-tok-${t.type}` : undefined"
      >{{ t.text }}</span
    ></code
  >
</template>

<style scoped>
/* Container — typography only. Surface styling (background, padding,
 * scroll, border-radius) is the caller's responsibility so this same
 * component drops into a queries-tab card, an inline code preview, a
 * full-bleed monaco-style block, etc., without fighting opinionated
 * defaults. */
.sql-block {
  font-family: var(--mono, ui-monospace, "SF Mono", Menlo, Consolas, monospace);
  font-size: inherit;
  line-height: inherit;
  white-space: inherit;
  word-break: inherit;
}

/* Token base — a hook for any caller that wants to add a hover/select
 * affordance across all highlighted tokens at once (e.g. dim everything
 * except keywords). No defaults set here. */
.sql-tok {
  /* intentionally empty — see comment above */
}

/* Keyword: brand-tinted, weight-bumped. `--mint-deep` is the runtime's
 * primary accent; the hex fallback applies in untrusted host pages
 * where that variable isn't defined. */
.sql-tok-keyword {
  color: var(--mint-deep, #0f766e);
  font-weight: 600;
}

/* String literal: warm green. Hardcoded — no var in the runtime palette
 * lines up with "successful literal" semantically, and `--pos` isn't
 * always available outside the SPA shell. */
.sql-tok-string {
  color: #2e7d32;
}

/* Numeric literal: amber. Distinct enough from the string colour that
 * `WHERE col = '42'` reads differently from `WHERE col = 42`. */
.sql-tok-number {
  color: #b45309;
}

/* Comment: muted + italic. `--card-muted` is set by the embed chrome;
 * fallback matches the existing queries-tab muted tone. */
.sql-tok-comment {
  color: var(--card-muted, #6b7280);
  font-style: italic;
}

/* Positional placeholder `$1` / `$2` / …: purple. Visually distinct
 * from both the string and number colours so a query that's still
 * carrying placeholders (pre-substitution) is unambiguous at a glance. */
.sql-tok-placeholder {
  color: #9333ea;
  font-weight: 600;
}

/* Dark-mode overrides. Mirrors the pattern used in the queries-tab
 * styles: a `:global(html[data-theme="dark"])` selector reaches out
 * of the scoped subtree to detect the shell's theme switch. The light-
 * mode greens / ambers / purples wash out on a dark background; the
 * dark overrides lift each toward a higher-luminance variant. */
:global(html[data-theme="dark"]) .sql-tok-keyword {
  color: var(--mint, #6acebf);
}
:global(html[data-theme="dark"]) .sql-tok-string {
  color: #86d391;
}
:global(html[data-theme="dark"]) .sql-tok-number {
  color: #fbbf24;
}
:global(html[data-theme="dark"]) .sql-tok-placeholder {
  color: #c084fc;
}
</style>
